/**
 * @fileoverview usePageTitle - Dynamic Page Title Management Hook
 *
 * Automatically updates the browser tab title based on the current route.
 * Uses the pageTitleManager utility to determine appropriate titles.
 *
 * @module hooks/usePageTitle
 * @see {@link utils/pageTitleManager} for title resolution logic
 */

import { useEffect } from 'react';

import { updatePageTitle } from 'utils/pageTitleManager';

/**
 * Hook to automatically update page title based on current route.
 *
 * Call this hook once in your app root or layout component.
 * It will automatically update the document title on:
 * - Initial mount
 * - Browser navigation (back/forward)
 *
 * Note: This hook does not return a value. It manages the document.title
 * as a side effect.
 *
 * @returns {void}
 *
 * @example
 * // In your app root or layout
 * function App() {
 *   usePageTitle(); // Automatically manages page title
 *
 *   return (
 *     <Routes>
 *       <Route path="/" element={<Home />} />
 *       <Route path="/settings" element={<Settings />} />
 *     </Routes>
 *   );
 * }
 *
 * @example
 * // In a layout wrapper
 * function MainLayout({ children }) {
 *   usePageTitle();
 *
 *   return (
 *     <div className="layout">
 *       <Header />
 *       <main>{children}</main>
 *       <Footer />
 *     </div>
 *   );
 * }
 */
export const usePageTitle = (): void => {
  useEffect(() => {
    // Update the initial title
    updatePageTitle();

    // Add a listener for URL changes
    const handleLocationChange = () => {
      updatePageTitle();
    };

    window.addEventListener('popstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);
};
